import { isDemoMode } from '../admin/demoMode';
import { getDemoResponse } from '../admin/demoData';

const BASE = '/api';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

/**
 * Demo mode gate. Runs before every request so a screen nobody thought about cannot leak real
 * customer data onto a projector, and so no click in front of an audience can mutate production.
 *
 * /auth/* is deliberately excluded: demo mode is a presentation skin, not a way to browse the
 * admin panel without logging in. Public shop endpoints are untouched.
 */
function demoIntercept<T>(path: string, method: string): T | undefined {
  if (!isDemoMode()) return undefined;
  if (path.startsWith('/auth/')) return undefined;

  const isAdminPath = path.startsWith('/admin/');
  // updateOrder() patches /orders/:id, so an admin write lives outside the /admin prefix.
  const isOrderPatch = method !== 'GET' && /^\/orders\/[^/]+$/.test(path);

  if (method !== 'GET' && (isAdminPath || isOrderPatch)) {
    throw new ApiError(409, 'demo_mode_read_only', {
      error: 'demo_mode_read_only',
      message: 'Tryb demo: zmiany są wyłączone.',
    });
  }

  if (method === 'GET' && isAdminPath) {
    const fixture = getDemoResponse(path);
    if (fixture !== undefined) return fixture as T;
    // An admin read with no fixture must not fall through to the network, or the presentation
    // would show real data on whichever screen was missed.
    throw new ApiError(409, 'demo_mode_no_fixture', {
      error: 'demo_mode_no_fixture',
      message: 'Tryb demo: ten ekran nie ma danych przykładowych.',
    });
  }

  return undefined;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';
  const demo = demoIntercept<T>(path, method);
  if (demo !== undefined) return demo;

  const res = await fetch(BASE + path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let message = res.statusText;
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
      message = (body.error as string) ?? (body.message as string) ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message, body);
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  postCsv: <T,>(path: string, csv: string) =>
    request<T>(path, {
      method: 'POST',
      body: csv,
      headers: { 'content-type': 'text/csv; charset=utf-8' },
    }),
  patch: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
};
